import { onMount } from 'svelte';
import { writable, get } from 'svelte/store';
import type { CountryCode } from 'svelte-tel-input/types';
import type { Location, SelectOption } from '../types';
import { LocationsService } from '../service/locations.service';
import { FormService } from '../service/form.service';
import { mapCountryNameToCode, REGIONS } from '../enquiry.util';
import { errorStore } from '../enquiry.store';
import type { Enquiry } from '../enquiry.schema';

export function useEnquiryForm() {
  const allLocations = writable<Location[]>([]);
  const filteredLocations = writable<Location[]>([]);
  const selectedRegion = writable<SelectOption | null>(null);
  const selectedLocation = writable<SelectOption | null>(null);
  const selectedCountry = writable<CountryCode | null>(null);
  const loading = writable(true);
  const sameCountryMessage = writable<string | null>(null);
  const legitimateInterest = writable(false);
  const phoneValid = writable(true);
  const phoneError = writable<string | undefined>(undefined);
  const showThankYouPopup = writable(false);
  const thankYouData = writable({ firstName: '', location: '' });

  const initializeLocations = async () => {
    loading.set(true);
    try {
      // Try to fetch from API first
      const apiLocations = await LocationsService.fetchLocations();
      if (apiLocations && apiLocations.length > 0) {
        const processed = LocationsService.processAPILocations(apiLocations);
        allLocations.set(processed);
        console.log('Loaded locations:', processed);
      } else {
        console.warn('API returned no locations');
        allLocations.set([]);
      }
    } catch (error) {
      console.error('Error initializing locations:', error);
      allLocations.set([]);
    } finally {
      loading.set(false);
    }
  };

  function filterLocationsByRegion(regionKey: string) {
    const currentLocations = get(allLocations);
    console.log('Filtering locations for region:', regionKey);
    console.log('All locations:', currentLocations);

    if (!regionKey || currentLocations.length === 0) {
      console.log('No region selected or no locations available');
      return [];
    }

    // First try to filter by region in address
    let filtered = currentLocations.filter((location) => {
      return location.address?.region === regionKey;
    });

    // If no locations found by region, try to filter by country
    if (filtered.length === 0) {
      // Map regions to countries
      const regionCountries: Record<string, string[]> = {
        'United States': ['United States', 'USA'],
        'United Kingdom': ['United Kingdom', 'UK'],
        Australia: ['Australia'],
        Singapore: ['Singapore'],
        'Hong Kong': ['Hong Kong'],
        Dubai: ['Dubai', 'United Arab Emirates'],
        Netherlands: ['Netherlands']
      };

      const countriesToMatch = regionCountries[regionKey] || [];

      filtered = currentLocations.filter((location) => {
        return countriesToMatch.includes(location.address?.country);
      });
    }

    // If still no locations found, try to match by location name
    if (filtered.length === 0) {
      filtered = currentLocations.filter((location) => {
        const locationName = location.location_name.toLowerCase();
        const regionNameLower = regionKey.toLowerCase();
        return locationName.includes(regionNameLower);
      });
    }

    console.log('Filtered locations:', filtered);
    return filtered;
  }

  function handleRegionChange(regionKey: string) {
    console.log('Region change:', regionKey);

    // Find the region object from REGIONS
    const region = REGIONS.find((r) => r.key === regionKey);
    if (!region) {
      console.error('Selected region not found:', regionKey);
      return;
    }

    // Update the selectedRegion store
    selectedRegion.set(region);
    console.log('Selected region:', region);

    // Map the selected region to its country code
    const countryCode = mapCountryNameToCode(region.key);

    // Update selectedCountry with the mapped country code
    if (countryCode) {
      selectedCountry.set(countryCode);
      console.log('Set country code:', countryCode);
    }

    // Filter locations based on the selected region
    const filtered = filterLocationsByRegion(region.key);
    filteredLocations.set(filtered);

    // Reset the selected location when region changes
    selectedLocation.set(null);

    // Reset legitimate interest when region changes
    legitimateInterest.set(false);
  }

  const handleLocationChange = (locationId: string) => {
    const currentFilteredLocations = get(filteredLocations);
    console.log('Location change:', locationId);
    console.log('Available locations:', currentFilteredLocations);

    const location = currentFilteredLocations.find((loc) => loc.location_id === locationId);

    if (location) {
      console.log('Selected location:', location);
      selectedLocation.set({
        key: location.location_id,
        value: location.location_name
      });

      // Update country based on location
      const countryCode = mapCountryNameToCode(location.address.country);
      if (countryCode) {
        selectedCountry.set(countryCode);
      }

      // Set legitimate interest to true when a location is selected
      legitimateInterest.set(true);
    } else {
      console.error('Location not found:', locationId);
    }
  };

  const handleSubmit = async (formData: Omit<Enquiry, 'token'>) => {
    try {
      const token = await FormService.executeRecaptcha();
      const completeData: Enquiry = { ...formData, token };

      const submitted = await FormService.submitForm(completeData);
      if (submitted) {
        // Open the thank you page in a new tab/window
        const thankYouUrl = `https://ultimateperformance.com/thank-you-pt/?sub=true&up_wp_location_id=0&greeting=${formData.first_name}&location=${formData.location_id || ''}&enquiry_id=${formData.enquiry_id}`;
        window.open(thankYouUrl, '_blank');
      }
      return submitted;
    } catch (error) {
      console.error('Error submitting form:', error);
      errorStore.set('Failed to submit form. Please try again.');
      return false;
    }
  };

  onMount(() => {
    FormService.loadRecaptchaScript();
    initializeLocations();
    filteredLocations.set([]);
  });

  return {
    allLocations,
    filteredLocations,
    selectedRegion,
    selectedLocation,
    selectedCountry,
    loading,
    sameCountryMessage,
    legitimateInterest,
    phoneValid,
    phoneError,
    showThankYouPopup,
    thankYouData,
    REGIONS,
    initializeLocations,
    handleRegionChange,
    handleLocationChange,
    handleSubmit
  };
}
